"use client"

import { Suspense, useState, useEffect, useRef } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import Link from "next/link"
import { ArrowRight } from "lucide-react"
import { PaymentSuccessModal } from "@/components/modal-dialogs/payment-success-modal/payment-success-modal"
import { TemplateGallery } from "@/components/dashboard/templates/template-gallery"
import type { TemplateItem } from "@/app/store/api/templateApi"
import { ReferralCodeDialog } from "@/components/modal-dialogs/referral-code-dialog"
import { GreetingSection } from "@/components/dashboard/home/greeting-section"
import { PromptBar, type PromptFile } from "@/components/dashboard/home/prompt-bar"
import { DeckTypeChips } from "@/components/dashboard/home/deck-type-chips"
import type { DeckType } from "@/components/dashboard/home/deck-type-chips"
import type { KnowledgeBaseFile } from "@/app/store/api/knowledgeBaseApi"
import { useAppSelector } from "@/app/store/hooks"
import { useDispatch } from "react-redux"
import { useCreateBlankDeckMutation } from "@/app/store/api/pitchDeckApi"
import { useUploadToKnowledgeBaseMutation } from "@/app/store/api/knowledgeBaseApi"
import { toast } from "sonner"
import { setProjectId, setDeckType, resetCreateDeck, setPromptDetails } from "@/app/store/slices/createDeckSlice"
import Footer from "@/components/public-layout/footer"
import { DashboardSidebar } from "@/components/dashboard/sidebar"
import { DashboardHeader } from "@/components/dashboard/dashboard-header"
import { DeckLimitModal } from "@/components/modal-dialogs/deck-limit-modal"
import { ProductHuntCard } from "@/components/dashboard/product-hunt-card"
import { trackEvent } from "@/lib/mixpanel"
import dynamic from "next/dynamic"

const LoginPopover = dynamic(
    () => import("@/components/auth/login-popover").then((mod) => mod.LoginPopover),
    { ssr: false }
)

const AnnouncementModal = dynamic(
    () => import("@/components/public/home/announcement-modal").then((mod) => mod.AnnouncementModal),
    { ssr: false }
)


const REFERRAL_DIALOG_STORAGE_KEY = 'deckwale_referral_dialog_seen'



function HomeContent() {
    const topRef = useRef<HTMLDivElement>(null)
    const router = useRouter()
    const dispatch = useDispatch()
    const [createBlankDeck, { isLoading: isCreatingBlankDeck }] = useCreateBlankDeckMutation()
    const [uploadToKnowledgeBase] = useUploadToKnowledgeBaseMutation()
    const [showReferralDialog, setShowReferralDialog] = useState(false)
     const { user, isAuthenticated, token } = useAppSelector((state) => state.auth)
     const isLoggedIn = !!(token && isAuthenticated)
     const [isLoginPopoverOpen, setIsLoginPopoverOpen] = useState(false)
    const [deckLimitModal, setDeckLimitModal] = useState<{ open: boolean; used?: number; total?: number; deckType?: string }>({ open: false })
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [purchasedSummary, setPurchasedSummary] = useState<{ name: string; quantity: number }[]>([])

    const [selectedDeckType, setSelectedDeckType] = useState<DeckType>("Presentation")
    const [selectedDocumentSubtype, setSelectedDocumentSubtype] = useState<string | null>(null)
    const [attachedFiles, setAttachedFiles] = useState<PromptFile[]>([])
    const [logoFile, setLogoFile] = useState<File | null>(null)
    const [prompt, setPrompt] = useState("")
    const [deepResearch, setDeepResearch] = useState(false)
    const promptDetails = useAppSelector((state) => state.createDeck.promptDetails)
    const searchParams = useSearchParams()

    const [pitchDeckDetails, setPitchDeckDetails] = useState({
        companyName: "",
        website: "",
        industry: "",
        stage: "",
        slideCount: "Auto"
    })

    const handlePitchDeckDataChange = (field: string, value: string) => {
        setPitchDeckDetails(prev => ({ ...prev, [field]: value }))
    }

    // Payment Success check
    useEffect(() => {
        if (searchParams.get("payment_success") === "1") {
            try {
                const dataStr = sessionStorage.getItem('deckwale_payment_success_data')
                if (dataStr) {
                    setPurchasedSummary(JSON.parse(dataStr))
                    setShowSuccessModal(true)
                    sessionStorage.removeItem('deckwale_payment_success_data')
                }
            } catch (e) {
                console.error("Failed to parse payment success data", e)
            }
            router.replace('/', { scroll: false })
        }
    }, [searchParams, router])

    const handleGoHome = () => {
        setShowSuccessModal(false)
    }

    // Handle initial selection from query params
useEffect(() => {
    const type = searchParams.get("type") as DeckType | null
    const subtype = searchParams.get("subtype")

    if (type) {
        setSelectedDeckType(type)
        if (type === "fundraising-pitch-deck" || type === "business-pitch-deck") {
            setDeepResearch(true)
        }
        if (type === "documents" && subtype) {
            setSelectedDocumentSubtype(subtype)
        } else {
            setSelectedDocumentSubtype(null)
        }
    } else {
        // No type in URL → set default
        router.replace(`?type=Presentation`, { scroll: false })
    }
}, [searchParams])

    // Clear Redux createDeck state whenever user comes to home page
    useEffect(() => {
        dispatch(resetCreateDeck())
    }, [dispatch])

    // Show referral dialog for logged-in users who haven't seen it yet
    useEffect(() => {
        const hasSeenDialog = localStorage.getItem(REFERRAL_DIALOG_STORAGE_KEY)
        if (!hasSeenDialog && user) {
            const timer = setTimeout(() => {
                setShowReferralDialog(true)
            }, 500)
            return () => clearTimeout(timer)
        }
    }, [user])

    const handleReferralDialogClose = () => {
        setShowReferralDialog(false)
        localStorage.setItem(REFERRAL_DIALOG_STORAGE_KEY, 'true')
    }

    const handleReferralApply = () => {
        localStorage.setItem(REFERRAL_DIALOG_STORAGE_KEY, 'true')
    }

    const handleReferralSkip = () => {
        localStorage.setItem(REFERRAL_DIALOG_STORAGE_KEY, 'true')
    }

    const handleSelectDeckType = (type: DeckType) => {
    setSelectedDeckType(type)
    if (type === "fundraising-pitch-deck" || type === "business-pitch-deck") {
        setDeepResearch(true)
    }
    
    if (type !== "documents") {
        setSelectedDocumentSubtype(null)
        router.replace(`?type=${type}`, { scroll: false })
    } else {
        router.replace(`?type=${type}`, { scroll: false })
    }
}

    const handleDeepResearchClick = () => {
        setDeepResearch(true)
    }

    const handleFastSearchClick = () => {
        setDeepResearch(false)
    }

    const handleUseTemplate = async (template: any) => {
        topRef.current?.scrollIntoView({ behavior: "smooth" })
        const effectiveId = template.templateId || template._id;
        
        trackEvent("Template Selected", {
            template_id: effectiveId,
            template_name: template.name,
            deck_type: selectedDeckType,
        })
        
        // Use docxUrl or documentTemplate to identify document templates
        const isDocument = !!(template.docxUrl || template.documentTemplate);
        
        if (isDocument) {
            setSelectedDeckType("documents")
            // Try to infer subtype from category or name if possible, or just stay as is
            const normalizedCategory = template.category?.toLowerCase();
            if (normalizedCategory && ["resume", "nda", "partnership-proposal", "business-document", "service-agreement", "custom"].includes(normalizedCategory)) {
                setSelectedDocumentSubtype(normalizedCategory)
            }
        } else if (selectedDeckType === "documents" || selectedDeckType === "deep-research") {
            handleSelectDeckType("Presentation")
        }

        const thumbnail = template.thumbnailUrl || template.documentTemplate?.thumbnailUrl || template.slideTemplates?.[0]?.thumbnailUrl || ""
        dispatch(setPromptDetails({
            prompt: prompt || "",
            fileNames: attachedFiles.map(f => (f as File).name || (f as KnowledgeBaseFile).fileName),
            userRequestedDeepResearch: deepResearch,
            templateId: effectiveId,
            templateName: template.name,
            templateThumbnail: thumbnail,
            templateCategory: template.category,
        }))
    }

    const handleRemoveTemplate = () => {
        dispatch(setPromptDetails({
            ...promptDetails,
            templateId: undefined,
            templateName: undefined,
            templateThumbnail: undefined,
            templateCategory: undefined,
        }))
    }

    const isDeckLimitError = (error: any): boolean => {
        const status = error?.status || error?.data?.statusCode
        const message = (error?.data?.message || error?.message || "").toLowerCase()
        return status === 403 || status === 429 ||
            message.includes("limit") || message.includes("quota") ||
            message.includes("exceeded") || message.includes("insufficient") ||
            message.includes("upgrade") || message.includes("no remaining")
    }

    const showDeckLimitError = (error: any, type?: string) => {
        const data = error?.data?.data || error?.data
        setDeckLimitModal({
            open: true,
            used: data?.used,
            total: data?.total,
            deckType: type,
        })
    }

    const handleSubmit = async () => {
        // If not logged in, save prompt to localStorage and redirect to login
        if (!isLoggedIn) {
            localStorage.setItem('deckwale_pending_prompt', JSON.stringify({
                prompt,
                timestamp: Date.now(),
            }))
            setIsLoginPopoverOpen(true)
            return
        }

        // Separate local files and knowledge base IDs
        const localFiles = attachedFiles.filter((f): f is File => f instanceof File);
        const kbIds = attachedFiles
            .filter((f): f is KnowledgeBaseFile => !(f instanceof File))
            .map(f => f._id);

        // Upload local attached files to Knowledge Base implicitly 
        if (localFiles.length > 0) {
            uploadToKnowledgeBase(localFiles).catch(err => console.error("Failed implicit knowledge base upload:", err))
        }

        const commonParams = {
            files: localFiles,
            knowledgeBaseIds: kbIds,
            ...(logoFile && { logo: logoFile }),
            userPrompt: prompt,
        };

        const fileNames = attachedFiles.map(f => (f as File).name || (f as KnowledgeBaseFile).fileName);

        switch (selectedDeckType) {
            case "Presentation":
                try {
                    const response = await createBlankDeck({
                        ...commonParams,
                        templateId: promptDetails.templateId,
                        presentationType: "Blank Deck",
                        deckType: deepResearch ? "PRO" : "FAST",
                    }).unwrap()
                    if (response.data?._id) {
                        const projectId = response.data._id
                        dispatch(setProjectId(projectId))
                        dispatch(setDeckType("blank-deck"))
                        trackEvent("Deck Created", {
                            deck_type: "Presentation",
                            research_mode: deepResearch ? "PRO" : "FAST",
                            template_used: !!promptDetails.templateId,
                            template_name: promptDetails.templateName || null,
                            has_files: attachedFiles.length > 0,
                            file_count: attachedFiles.length,
                            prompt_length: prompt.length,
                            project_id: projectId,
                        })
                        if (deepResearch) {
                            localStorage.setItem('deckwale_pending_deep_research', 'true')
                        }
                        dispatch(setPromptDetails({
                            ...promptDetails,
                            prompt,
                            fileNames,
                            userRequestedDeepResearch: deepResearch
                        }))
                        router.push(`/workspace/blank-deck/${projectId}`)
                    }
                } catch (error: any) {
                    if (isDeckLimitError(error)) {
                        showDeckLimitError(error, deepResearch ? "Pro" : "Fast")
                    } else {
                        const message = error?.data?.message || error?.message || "Failed to create deck. Please try again."
                        console.error("Failed to create blank deck:", message)
                        toast.error(message)
                    }
                }
                break
            case "documents":
                if (selectedDocumentSubtype) {
                    try {
                        const response = await createBlankDeck({
                            ...commonParams,
                            documentType: selectedDocumentSubtype,
                            companyName: `${selectedDocumentSubtype} Document`,
                            templateId: promptDetails.templateId,
                        }).unwrap()
                        if (response.data?._id) {
                            const projectId = response.data._id
                            dispatch(setProjectId(projectId))
                            dispatch(setDeckType(selectedDocumentSubtype))
                            trackEvent("Deck Created", {
                                deck_type: "Document",
                                document_subtype: selectedDocumentSubtype,
                                template_used: !!promptDetails.templateId,
                                template_name: promptDetails.templateName || null,
                                has_files: attachedFiles.length > 0,
                                file_count: attachedFiles.length,
                                prompt_length: prompt.length,
                                project_id: projectId,
                            })
                           
                            dispatch(setPromptDetails({
                                ...promptDetails,
                                prompt,
                                fileNames,
                            }))
                            router.push(`/workspace/${selectedDocumentSubtype}/${projectId}`)
                        }
                    } catch (error: any) {
                        if (isDeckLimitError(error)) {
                            showDeckLimitError(error, "document")
                        } else {
                            const message = error?.data?.message || error?.message || "Failed to create document. Please try again."
                            console.error("Failed to create document project:", message)
                            toast.error(message)
                        }
                    }
                }
                break
            case "fundraising-pitch-deck":
                try {
                    const response = await createBlankDeck({
                        ...commonParams,
                        templateId: promptDetails.templateId,
                        companyName: pitchDeckDetails.companyName,
                        companyStage: pitchDeckDetails.stage,
                        industry: pitchDeckDetails.industry,
                        website: pitchDeckDetails.website,
                        numberOfSlides: pitchDeckDetails.slideCount,
                        presentationType: "Fundraising Pitch Deck",
                    }).unwrap()
                    if (response.data?._id) {
                        const projectId = response.data._id
                        dispatch(setProjectId(projectId))
                        dispatch(setDeckType("fundraising-pitch-deck"))
                        trackEvent("Deck Created", {
                            deck_type: "Fundraising Pitch Deck",
                            company_name: pitchDeckDetails.companyName,
                            company_stage: pitchDeckDetails.stage,
                            industry: pitchDeckDetails.industry,
                            has_website: !!pitchDeckDetails.website,
                            slide_count: pitchDeckDetails.slideCount,
                            template_used: !!promptDetails.templateId,
                            template_name: promptDetails.templateName || null,
                            has_files: attachedFiles.length > 0,
                            file_count: attachedFiles.length,
                            project_id: projectId,
                        })
                        dispatch(setPromptDetails({
                            ...promptDetails,
                            prompt: prompt,
                            fileNames,
                        }))
                        router.push(`/workspace/fundraising-pitch-deck/${projectId}`)
                    }
                } catch (error: any) {
                    if (isDeckLimitError(error)) {
                        showDeckLimitError(error, "Pitch Deck")
                    } else {
                        const message = error?.data?.message || error?.message || "Failed to create pitch deck. Please try again."
                        console.error("Failed to create pitch deck:", message)
                        toast.error(message)
                    }
                }
                break
            case "business-pitch-deck":
                try {
                    const response = await createBlankDeck({
                        ...commonParams,
                        templateId: promptDetails.templateId,
                        companyName: pitchDeckDetails.companyName,
                        companyStage: pitchDeckDetails.stage,
                        industry: pitchDeckDetails.industry,
                        website: pitchDeckDetails.website,
                        numberOfSlides: pitchDeckDetails.slideCount,
                        presentationType: "Business Pitch Deck",
                    }).unwrap()
                    if (response.data?._id) {
                        const projectId = response.data._id
                        dispatch(setProjectId(projectId))
                        dispatch(setDeckType("business-pitch-deck"))
                        trackEvent("Deck Created", {
                            deck_type: "Business Pitch Deck",
                            company_name: pitchDeckDetails.companyName,
                            company_stage: pitchDeckDetails.stage,
                            industry: pitchDeckDetails.industry,
                            has_website: !!pitchDeckDetails.website,
                            slide_count: pitchDeckDetails.slideCount,
                            template_used: !!promptDetails.templateId,
                            template_name: promptDetails.templateName || null,
                            has_files: attachedFiles.length > 0,
                            file_count: attachedFiles.length,
                            project_id: projectId,
                        })
                        dispatch(setPromptDetails({
                            ...promptDetails,
                            prompt: prompt,
                            fileNames,
                        }))
                        router.push(`/workspace/business-pitch-deck/${projectId}`)
                    }
                } catch (error: any) {
                    if (isDeckLimitError(error)) {
                        showDeckLimitError(error, "Business Pitch Deck")
                    } else {
                        const message = error?.data?.message || error?.message || "Failed to create business pitch deck. Please try again."
                        console.error("Failed to create business pitch deck:", message)
                        toast.error(message)
                    }
                }
                break
            case "deep-research":
                try {
                    const response = await createBlankDeck({
                        ...commonParams,
                        presentationType: "Research",
                    }).unwrap()
                    if (response.data?._id) {
                        const projectId = response.data._id
                        dispatch(setProjectId(projectId))
                        dispatch(setDeckType("deep-research"))
                        trackEvent("Deck Created", {
                            deck_type: "Deep Research",
                            has_files: attachedFiles.length > 0,
                            file_count: attachedFiles.length,
                            prompt_length: prompt.length,
                            project_id: projectId,
                        })
                        localStorage.setItem('deckwale_pending_deep_research', 'true')
                        dispatch(setPromptDetails({
                            ...promptDetails,
                            prompt,
                            fileNames,
                            userRequestedDeepResearch: true
                        }))
                        router.push(`/workspace/deep-research/${projectId}`)
                    }
                } catch (error: any) {
                    if (isDeckLimitError(error)) {
                        showDeckLimitError(error, "Research")
                    } else {
                        const message = error?.data?.message || error?.message || "Failed to start research. Please try again."
                        console.error("Failed to create research project:", message)
                        toast.error(message)
                    }
                }
                break
        }
    }

    const dashboardContent = (
        <div ref={topRef} className="min-h-full px-4 md:px-12 py-6 md:py-8">
            <div className="mx-auto max-w-7xl">
                <div className="flex flex-col items-center pt-8 md:pt-12">
                    <div className="w-full max-w-[50rem] mx-auto space-y-4 md:space-y-8">
                        <div className="mb-4 md:mb-11 text-center">
                            <GreetingSection selectedDeckType={selectedDeckType}/>
                        </div>
                        <PromptBar
                            selectedDeckType={selectedDeckType}
                            selectedDocumentSubtype={selectedDocumentSubtype}
                            onDeepResearchClick={handleDeepResearchClick}
                            onFastSearchClick={handleFastSearchClick}
                            onSubmit={handleSubmit}
                            isLoading={isCreatingBlankDeck}
                            files={attachedFiles}
                            onFilesChange={setAttachedFiles}
                            logoFile={logoFile}
                            onLogoChange={setLogoFile}
                            prompt={prompt}
                            onPromptChange={setPrompt}
                            selectedTemplate={
                                promptDetails.templateId
                                    ? {
                                        id: promptDetails.templateId,
                                        name: promptDetails.templateName || "",
                                        thumbnail: promptDetails.templateThumbnail,
                                    }
                                    : undefined
                            }
                            onRemoveTemplate={handleRemoveTemplate}
                            pitchDeckDetails={pitchDeckDetails}
                            onPitchDeckDataChange={handlePitchDeckDataChange}
                            onSwitchToFundraising={() => handleSelectDeckType("fundraising-pitch-deck")}
                        />
                        <DeckTypeChips
                            selectedDeckType={selectedDeckType}
                            onSelectDeckType={handleSelectDeckType}
                            onSelectDocumentSubtype={(subtype) => {
                                if (promptDetails.templateId && promptDetails.templateCategory && subtype) {
                                    if (promptDetails.templateCategory.toLowerCase() !== subtype.toLowerCase()) {
                                        dispatch(setPromptDetails({
                                            ...promptDetails,
                                            templateId: undefined,
                                            templateName: undefined,
                                            templateThumbnail: undefined,
                                            templateCategory: undefined
                                        }))
                                    }
                                }
                                setSelectedDocumentSubtype(subtype)
                                if (subtype) {
                                    router.replace(`?type=documents&subtype=${subtype}`, { scroll: false })
                                }
                            }}
                        />
                    </div>
                </div>

                {selectedDeckType !== "fundraising-pitch-deck" && selectedDeckType !== "business-pitch-deck" && selectedDeckType !== "deep-research" && (
                    <div className="mt-16 md:mt-24 pb-8 mx-auto max-w-7xl">
                        <TemplateGallery 
                            limit={8} 
                            showSearch 
                            onUseTemplate={handleUseTemplate} 
                            selectedDeckType={selectedDeckType}
                            selectedDocumentSubtype={selectedDocumentSubtype}
                        />
                    </div>
                )}

                <div className="w-full mt-12">
                    <hr className="border-slate-200" />
                </div>

                <div className="w-full mt-auto">
                    <Footer />
                </div>
            </div>

            <ReferralCodeDialog
                isOpen={showReferralDialog}
                onClose={handleReferralDialogClose}
                onApply={handleReferralApply}
                onSkip={handleReferralSkip}
                showApplyToAccount={true}
            />
        </div>
    )

    return (
        <div className="flex h-screen overflow-hidden bg-white md:bg-[#f4f4f4]">
            <div className="hidden md:flex h-full">
                <DashboardSidebar />
            </div>
            <div className="flex-1 flex flex-col h-full overflow-hidden md:py-3 md:pr-3">
                <main className="relative flex-1 flex flex-col h-full overflow-hidden md:rounded-xl md:bg-white bg-white md:border md:border-slate-200">
                    {/* Background Elements */}
                    <div className="absolute inset-0 pointer-events-none z-0 opacity-70">
                        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
                        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:120px_120px] opacity-80" />
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,white_90%)]" />
                    </div>

                    <div className="relative z-10">
                        <DashboardHeader />
                    </div>

                    <div className="flex-1 overflow-y-auto relative z-10">
                        {dashboardContent}
                    </div>
                </main>
            </div>

            <LoginPopover
                isOpen={isLoginPopoverOpen}
                onClose={() => setIsLoginPopoverOpen(false)}
            />
            <DeckLimitModal
                isOpen={deckLimitModal.open}
                onClose={() => setDeckLimitModal({ open: false })}
                used={deckLimitModal.used}
                total={deckLimitModal.total}
                deckType={deckLimitModal.deckType}
            />
            <PaymentSuccessModal
                isOpen={showSuccessModal}
                onClose={handleGoHome}
                summary={purchasedSummary}
            />
            <AnnouncementModal isReady={true} />
        </div>

    )
}

export default function RootPage() {
    return (
        <Suspense>
            <HomeContent />
        </Suspense>
    )
}
